/**
 * Set of pure functions for working with a tree of nested nodes of this shape:
 *
 *   [
 *     { children: [] }
 *   ]
 */

/**
 * Executes a callback on each node. Like Array.forEach, but for nested structures.
 * Callback looks like this:
 *
 *  @param  node  {Object} - The node
 *  @param  path  {Array}  - The "address", an array of indices in children arrays of its ancestors
 *  @param  depth {Number} - Its nesting depth
 *  @param  count {Number} - Global counter
 *  callback( node, path, depth, count )
 *
 * @param tree     {Array}     - The tree
 * @param callback {Function}  - Callback to be executed on each node
 * @param path     (Internal use, set recursively)
 * @param depth    (Internal use, set recursively)
 * @param extra    (Internal use, set recursively)
 */
const walk = (tree, callback, path = [0], depth = 0, extra = { count: 0 }) => {
  tree.forEach((child, index) => {
    path[depth] = index

    callback(child, [...path], depth, extra.count)

    extra.count++

    if (child.children) walk(child.children, callback, [...path], depth + 1, extra)
  })
}

const getNodeByPath = (tree, path) => {
  let node = tree[ path[0] ]

  for (let i = 1; i < path.length; i++) node = node.children[ path[i] ]
  return node
}

const getParent = (tree, path) => {
  const parentPath = path.slice(0, path.length - 1)

  return getNodeByPath(tree, parentPath)
}

const getAncestors = (tree, path) => {
  const ancestors = []
  const p = [ ...path ]

  for (let i = path.length; i > 1; i--) {
    p.pop()
    ancestors.push(getNodeByPath(tree, p))
  }
  return ancestors
}

/**
 * Excecutes a callback for each parent, passing all descendant leafs (nodes without children).
 * @TODO - Any better name? forEachParentGetDescendantLeafs() is a bit long
 *
 * Callback signature:
 *
 *   callback(
 *       parent,  // The parent node
 *       leafs    // All leafs down the branch, including children's children
 *   )
 *
 * Example:
 *
 *   ( node, leafs ) => console.log( node.title, '->', leafs.map( leaf => leaf.title ).join(',') )
 *
 *
 * @param {Array} tree
 * @param {Function} callback
 */
const getDescendantLeafs = (tree, callback) => {
  let levelLeafs = []

  tree.forEach(node => {
    if (node.children) {
      // Direct children leafs
      let leafs = node.children.filter(n => !n.children)

      // All descendant leafs
      leafs = leafs.concat(getDescendantLeafs(node.children, callback))

      callback(node, leafs)

      levelLeafs = levelLeafs.concat(leafs)
    }
  })

  return levelLeafs
}

export {
  walk,
  getNodeByPath,
  getParent,
  getAncestors,
  getDescendantLeafs
}
